import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close, Warning } from "@mui/icons-material";

/**
 * Reusable ConfirmDialog component for confirmation actions
 * 
 * @param {boolean} open - Dialog open state
 * @param {string} title - Dialog title
 * @param {string} message - Confirmation message
 * @param {Function} onClose - Close handler
 * @param {Function} onConfirm - Confirm handler
 * @param {boolean} loading - Loading state
 * @param {string} confirmLabel - Confirm button label (default: "Confirm")
 * @param {string} cancelLabel - Cancel button label (default: "Cancel")
 * @param {string} severity - Severity level (warning, error, info)
 * @param {ReactNode} icon - Custom icon
 */
const ConfirmDialog = ({
  open,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  onClose,
  onConfirm,
  loading = false,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  severity = "warning",
  icon,
}) => {
  const severityColors = {
    warning: "warning.main",
    error: "error.main",
    info: "info.main",
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose?.();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            py: 1,
          }}
        >
          {icon || (
            <Warning
              sx={{
                fontSize: 40,
                color: severityColors[severity] || severityColors.warning,
              }}
            />
          )}
          <Typography variant="body1">{message}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={severity === "error" ? "error" : severity === "info" ? "info" : "warning"}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;

